import React, { useEffect, useState } from "react";
import { gaTrackCta, gaTrackEvent } from "../../../analytics/ga4";
import { responsiveGridColumns } from "../../../core/breakpoints";
import { analyticsWindowOptions } from "../../../core/analyticsWindows";
import { C } from "../../../core/constants";
import { APP_FONT_SIZE } from "../../../core/theme";
import { useIsMobile } from "../../../hooks/useMediaQuery";
import useCreditsInsights from "../useCreditsInsights";
import Button from "../../../ui/Button";
import AnalyticsWindowSelector from "../../../ui/AnalyticsWindowSelector";
import GlassCard from "../../../ui/GlassCard";
import { CREDIT_PACK_BASE, PRODUCT_CODE_BY_PACK_KEY, SUBSCRIPTION_PRODUCT_CODE } from "../../billing/config";

export default function CreditsInsightsPage({
  acc,
  setPage,
  t,
  accountStatus,
  onSelectPack,
  onSelectSubscription,
  billingCatalog = [],
  locale,
  formatInTimezone,
}) {
  const isMobile = useIsMobile();
  const fs = APP_FONT_SIZE;
  const [showPaymentOptions, setShowPaymentOptions] = useState(false);
  const activeSubscription = Boolean(accountStatus?.activeSubscription);
  const { loading, loadingMore, error, data, windowKey, setWindowKey, loadMoreActivity, hasData } = useCreditsInsights(
    "30d",
    { activeSubscription }
  );
  useEffect(() => {
    gaTrackEvent("credits_insight_open");
  }, []);
  const kpis = data?.kpis || {};
  const featureBreakdown = data?.feature_breakdown || [];
  const recentActivity = data?.recent_activity || [];
  const activityPagination = data?.recent_activity_pagination || {};
  const efficiency = data?.efficiency || {};
  const freeQuotaStatus = data?.free_quota_status || {};
  const freeQuotaTotals = data?.free_quota_totals || {};

  const interpolate = (template, vars = {}) =>
    String(template || "").replace(/%\{(\w+)\}/g, (_, key) => (
      Object.prototype.hasOwnProperty.call(vars, key) ? String(vars[key]) : `%{${key}}`
    ));

  const formatTransactionReason = (reason) => {
    const raw = String(reason || "").trim();
    if (!raw) return t("credits_transaction_reason_unknown");
    const key = `credits_transaction_reason_${raw}`;
    const translated = t(key, "");
    if (translated) return translated;
    return raw.replace(/_/g, " ");
  };

  const nebulaQuickTopUpCredits = CREDIT_PACK_BASE.find((p) => p.key === "nebula")?.credits ?? 25;

  const quotaCards = [
    { key: "zodiac", label: t("credits_quota_zodiac"), description: t("credits_quota_zodiac_desc") },
    { key: "tarot", label: t("credits_quota_tarot"), description: t("credits_quota_tarot_desc") },
    { key: "wedding", label: t("credits_quota_wedding"), description: t("credits_quota_wedding_desc") },
  ];

  const formatMoney = (amountMinor, currency) => {
    if (!Number.isFinite(amountMinor)) return "--";
    const normalizedCurrency = (currency || "VND").toUpperCase();
    const fractionDigits = normalizedCurrency === "USD" ? 2 : 0;
    const displayAmount = normalizedCurrency === "USD" ? amountMinor / 100 : amountMinor;
    try {
      return new Intl.NumberFormat(locale === "vi" ? "vi-VN" : "en-US", {
        style: "currency",
        currency: normalizedCurrency,
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
      }).format(displayAmount);
    } catch (_) {
      return `${displayAmount}`;
    }
  };

  const packColorByCode = {
    stardust: acc.tertiary,
    nebula: acc.primary,
    supernova: acc.secondary,
  };
  const preferredCurrency = locale === "vi" ? "VND" : "USD";
  const pickPrice = (prices = [], billingInterval) => (
    prices.find((row) => row.currency === preferredCurrency && row.billing_interval === billingInterval) ||
    prices.find((row) => row.currency === "VND" && row.billing_interval === billingInterval) ||
    prices.find((row) => row.currency === "USD" && row.billing_interval === billingInterval) ||
    prices.find((row) => row.billing_interval === billingInterval)
  );
  const packs = billingCatalog
    .filter((item) => item.product_type === "credit_pack")
    .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
    .map((item) => {
      const price = pickPrice(item.prices || [], "one_time");
      return {
        code: item.code,
        name: t(`pack_${item.code}`),
        credits: item.credits_amount || 0,
        amountMinor: price?.amount_minor,
        currency: price?.currency || "VND",
        displayPrice: formatMoney(price?.amount_minor, price?.currency || "VND"),
        color: packColorByCode[item.code] || acc.primary,
      };
    });
  const fallbackCurrency = locale === "en" ? "USD" : "VND";
  const fallbackSubscriptionAmountMinor = fallbackCurrency === "USD" ? 999 : 199000;
  const fallbackPacks = packs.length > 0 ? packs : [
    {
      code: "stardust",
      name: t("pack_stardust"),
      credits: 10,
      amountMinor: fallbackCurrency === "USD" ? 299 : 59000,
      currency: fallbackCurrency,
      displayPrice: formatMoney(fallbackCurrency === "USD" ? 299 : 59000, fallbackCurrency),
      color: acc.tertiary,
    },
    {
      code: "nebula",
      name: t("pack_nebula"),
      credits: 25,
      amountMinor: fallbackCurrency === "USD" ? 599 : 119000,
      currency: fallbackCurrency,
      displayPrice: formatMoney(fallbackCurrency === "USD" ? 599 : 119000, fallbackCurrency),
      color: acc.primary,
    },
    {
      code: "supernova",
      name: t("pack_supernova"),
      credits: 70,
      amountMinor: fallbackCurrency === "USD" ? 1399 : 279000,
      currency: fallbackCurrency,
      displayPrice: formatMoney(fallbackCurrency === "USD" ? 1399 : 279000, fallbackCurrency),
      color: acc.secondary,
    },
  ];
  return (
    <div style={{ padding: "24px 32px 40px" }}>
      <GlassCard style={{ padding: "16px 18px", marginBottom: 20 }} accent={accountStatus?.activeSubscription ? acc.secondary : acc.primary}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexDirection: isMobile ? "column" : "row",
            gap: 14,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: 12,
                display: "grid",
                placeItems: "center",
                fontSize: 18,
                color: accountStatus?.activeSubscription ? acc.secondary : acc.primary,
                background: accountStatus?.activeSubscription ? `${acc.secondary}18` : `${acc.primary}18`,
                border: `1px solid ${accountStatus?.activeSubscription ? acc.secondary : acc.primary}33`,
                flexShrink: 0,
              }}
              aria-hidden
            >
              ☾
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 22, color: C.textPrimary, lineHeight: 1.2 }}>
                {t("credits_reading_history_title")}
              </div>
              <div style={{ color: C.textSecondary, fontSize: 13, marginTop: 4, lineHeight: 1.45 }}>
                {accountStatus?.activeSubscription
                  ? t("credits_reading_history_subscriber_note")
                  : t("credits_reading_history_body")}
              </div>
            </div>
          </div>
          {accountStatus?.activeSubscription ? (
            <Button
              variant="outline"
              acc={acc}
              onClick={() => {
                gaTrackCta({
                  cta_id: "credits_view_reading_history",
                  cta_label: t("credits_reading_history_cta_view"),
                  destination: "readingHistory",
                  screen_class: "creditsPlans",
                });
                setPage("readingHistory");
              }}
              style={{ padding: "9px 16px", fontSize: 12, whiteSpace: "nowrap", width: isMobile ? "100%" : "auto" }}
            >
              {t("credits_reading_history_cta_view")} →
            </Button>
          ) : (
            <Button
              variant="primary"
              acc={acc}
              onClick={() => {
                gaTrackCta({
                  cta_id: "credits_reading_history_subscribe",
                  cta_label: t("credits_reading_history_cta_subscribe"),
                  destination: "modal_payment_options",
                  screen_class: "creditsPlans",
                  product_code: SUBSCRIPTION_PRODUCT_CODE,
                  product_type: "subscription",
                });
                onSelectSubscription?.();
              }}
              style={{ padding: "9px 16px", fontSize: 12, whiteSpace: "nowrap", width: isMobile ? "100%" : "auto" }}
            >
              {t("credits_reading_history_cta_subscribe")}
            </Button>
          )}
        </div>
      </GlassCard>

      <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 20 }}>{t("credits_insights_subtitle")}</div>

      {loading && !hasData && <GlassCard style={{ padding: 18, marginBottom: 16 }}>{t("credits_insights_loading")}</GlassCard>}
      {!loading && !hasData && error && <GlassCard style={{ padding: 18, marginBottom: 16 }}>{t(error)}</GlassCard>}

      {hasData && !error && (
        <GlassCard style={{ padding: 20, marginBottom: 20 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, flexWrap: "wrap" }}>
            <div style={{ maxWidth: 520 }}>
              <div style={{ fontSize: 12, letterSpacing: "0.12em", color: C.textMuted, marginBottom: 10 }}>{t("credits_quota_summary_eyebrow")}</div>
              <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 34, color: C.textPrimary }}>
                {interpolate(t("credits_quota_summary_title"), {
                  remaining: freeQuotaTotals.remaining_total ?? 0,
                  total: freeQuotaTotals.limit_total ?? 0,
                })}
              </div>
              <div style={{ color: C.textSecondary, fontSize: fs - 2, marginTop: 6 }}>
                {t("credits_quota_summary_body")}
              </div>
            </div>

            <div style={{ display: "grid", gap: 10, minWidth: 280, flex: "1 1 320px" }}>
              {quotaCards.map((card) => {
                const bucket = freeQuotaStatus[card.key] || {};
                return (
                  <div
                    key={card.key}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 12,
                      padding: "12px 14px",
                      borderRadius: 14,
                      border: `1px solid ${acc.primary}22`,
                      background: "rgba(255,255,255,0.03)",
                    }}
                  >
                    <div>
                      <div style={{ color: C.textPrimary, fontSize: 14, fontWeight: 600 }}>{card.label}</div>
                      <div style={{ color: C.textMuted, fontSize: 12 }}>{card.description}</div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <div style={{ color: acc.primary, fontSize: 18, fontWeight: 700 }}>
                        {bucket.remaining_count ?? 0}/{bucket.limit_count ?? 0}
                      </div>
                      <div style={{ color: C.textMuted, fontSize: 11 }}>
                        {interpolate(t("credits_quota_used_label"), { count: bucket.used_count ?? 0 })}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div
            style={{
              marginTop: 18,
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 14,
              padding: "16px 18px",
              borderRadius: 16,
              border: `1px solid ${acc.primary}33`,
              background: `linear-gradient(135deg, ${acc.primary}12, ${acc.tertiary}10)`,
            }}
          >
            <div>
              <div style={{ color: C.textPrimary, fontSize: 15, fontWeight: 600 }}>{t("credits_cta_title")}</div>
              <div style={{ color: C.textSecondary, fontSize: 13, marginTop: 4 }}>{t("credits_cta_body")}</div>
            </div>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              <Button
                variant="primary"
                acc={acc}
                onClick={() => {
                  gaTrackCta({
                    cta_id: "credits_quick_topup_nebula",
                    cta_label: t("credits_action_quick_top_up"),
                    destination: "/payment",
                    screen_class: "creditsPlans",
                    product_code: PRODUCT_CODE_BY_PACK_KEY.nebula,
                    product_type: "credit_pack",
                  });
                  setPage("payment", {
                    query: {
                      product_code: PRODUCT_CODE_BY_PACK_KEY.nebula,
                      product_type: "credit_pack",
                    },
                  });
                }}
                style={{ padding: "12px 20px", fontSize: 14, minHeight: 46, boxShadow: `0 12px 28px ${acc.primary}22` }}
              >
                {interpolate(t("credits_action_quick_top_up"), { credits: nebulaQuickTopUpCredits })}
              </Button>
              <Button
                variant="outline"
                acc={acc}
                onClick={() => {
                  gaTrackCta({
                    cta_id: "credits_view_pricing_modal",
                    cta_label: t("credits_action_view_pricing"),
                    destination: "modal_payment_options",
                    screen_class: "creditsPlans",
                  });
                  setShowPaymentOptions(true);
                }}
                style={{ padding: "12px 20px", fontSize: 14, minHeight: 46, background: `${acc.primary}10`, border: `1px solid ${acc.primary}66` }}
              >
                {t("credits_action_view_pricing")}
              </Button>
            </div>
          </div>
        </GlassCard>
      )}

      <AnalyticsWindowSelector
        windowKey={windowKey}
        options={analyticsWindowOptions(activeSubscription)}
        onChange={setWindowKey}
        loading={loading}
        hasData={hasData}
        showLoadingHint
        acc={acc}
        t={t}
      />

      <div style={{ opacity: loading && hasData ? 0.65 : 1, pointerEvents: loading && hasData ? "none" : "auto" }}>
        <div
          className="ecliptica-card-grid"
          style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 14, marginBottom: 20 }}
        >
          {[
            { label: t("credits_kpi_spent"), val: kpis.credits_spent ?? 0, icon: "✦" },
            { label: t("credits_kpi_readings"), val: kpis.readings_count ?? 0, icon: "⊕" },
            { label: t("credits_kpi_avg"), val: kpis.avg_credits_per_reading ?? 0, icon: "◎" },
            { label: t("credits_kpi_streak"), val: `${kpis.streak_days ?? 0}d`, icon: "☾" },
          ].map((item, idx) => (
            <GlassCard key={idx} style={{ padding: 18 }}>
              <div style={{ color: acc.primary, marginBottom: 6 }}>{item.icon}</div>
              <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 32, color: C.textPrimary }}>{item.val}</div>
              <div style={{ fontSize: 12, color: C.textMuted }}>{item.label}</div>
            </GlassCard>
          ))}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: responsiveGridColumns(isMobile, { desktop: "1.1fr 1fr", mobile: "1fr" }), gap: 16, alignItems: "start" }}>
          <GlassCard style={{ padding: 20 }}>
            <div style={{ fontSize: 12, letterSpacing: "0.12em", color: C.textMuted, marginBottom: 10 }}>{t("credits_breakdown_title")}</div>
            {featureBreakdown.length === 0 ? (
              <div style={{ color: C.textSecondary, fontSize: fs - 1 }}>{t("credits_insights_empty")}</div>
            ) : (
              featureBreakdown.map((row) => (
                <div key={row.feature} style={{ marginBottom: 10 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 4 }}>
                    <span style={{ color: C.textSecondary }}>{t(`credits_feature_${row.feature}`)}</span>
                    <span style={{ color: acc.primary }}>{row.count} ({row.percentage}%)</span>
                  </div>
                  <div style={{ height: 8, borderRadius: 999, background: "rgba(255,255,255,0.08)", overflow: "hidden" }}>
                    <div style={{ width: `${Math.min(100, row.percentage || 0)}%`, height: "100%", background: `linear-gradient(90deg, ${acc.primary}, ${acc.tertiary})` }} />
                  </div>
                </div>
              ))
            )}
          </GlassCard>

          <GlassCard style={{ padding: 20 }}>
            <div style={{ fontSize: 12, letterSpacing: "0.12em", color: C.textMuted, marginBottom: 10 }}>{t("credits_efficiency_title")}</div>
            <div style={{ display: "grid", gap: 8, fontSize: 13 }}>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ color: C.textSecondary }}>{t("credits_efficiency_free_window")}</span>
                <span style={{ color: acc.primary }}>{efficiency.free_quota_readings ?? 0}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ color: C.textSecondary }}>{t("credits_efficiency_paid")}</span>
                <span style={{ color: acc.primary }}>{efficiency.paid_readings ?? 0}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ color: C.textSecondary }}>{t("credits_efficiency_paid_spent")}</span>
                <span style={{ color: acc.primary }}>{efficiency.paid_credits_spent ?? 0}</span>
              </div>
            </div>
            <div style={{ marginTop: 10, color: C.textMuted, fontSize: 12 }}>
              {interpolate(
                t(windowKey === "all" ? "credits_efficiency_window_note_all" : "credits_efficiency_window_note"),
                { window: t(`credits_window_${windowKey}`) }
              )}
            </div>
          </GlassCard>
        </div>

        <GlassCard style={{ padding: 20, marginTop: 16 }}>
          <div style={{ fontSize: 12, letterSpacing: "0.12em", color: C.textMuted, marginBottom: 10 }}>{t("credits_activity_title")}</div>
          {recentActivity.length === 0 ? (
            <div style={{ color: C.textSecondary, fontSize: fs - 1 }}>{t("credits_activity_empty")}</div>
          ) : (
            <>
              {recentActivity.map((item, idx) => (
                <div key={`${item.created_at}-${idx}`} style={{ display: "flex", justifyContent: "space-between", gap: 10, padding: "8px 0", borderBottom: idx === recentActivity.length - 1 ? "none" : "1px solid rgba(255,255,255,0.06)" }}>
                  <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <span style={{ color: C.textSecondary, fontSize: 13 }}>{formatTransactionReason(item.reason)}</span>
                    <span style={{ color: C.textMuted, fontSize: 11 }}>
                      {formatInTimezone(item.created_at, { locale, dateStyle: "medium", timeStyle: "short" })}
                    </span>
                  </div>
                  <span style={{ color: item.amount >= 0 ? acc.secondary : acc.primary, fontSize: 13, fontWeight: 600 }}>
                    {item.amount >= 0 ? "+" : ""}{item.amount}
                  </span>
                </div>
              ))}
              {activityPagination.has_more && (
                <div style={{ marginTop: 12 }}>
                  <Button
                    variant="outline"
                    acc={acc}
                    onClick={loadMoreActivity}
                    style={{ padding: "8px 14px", fontSize: 12, opacity: loadingMore ? 0.7 : 1, cursor: loadingMore ? "not-allowed" : "pointer" }}
                  >
                    {loadingMore ? t("credits_activity_loading_more") : t("credits_activity_load_more")}
                  </Button>
                </div>
              )}
            </>
          )}
        </GlassCard>
      </div>

      {showPaymentOptions && (
        <div
          onClick={() => setShowPaymentOptions(false)}
          style={{ position: "fixed", inset: 0, background: "rgba(5,8,24,0.72)", backdropFilter: "blur(4px)", zIndex: 120, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
        >
          <div
            onClick={(event) => event.stopPropagation()}
            style={{ width: "100%", maxWidth: 740, borderRadius: 16, border: `1px solid ${acc.primary}44`, background: "rgba(12,18,40,0.96)", padding: 20 }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
              <h3 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 30, color: C.textPrimary }}>{t("credits_modal_title")}</h3>
              <button type="button" onClick={() => setShowPaymentOptions(false)} style={{ border: "none", background: "transparent", color: C.textMuted, cursor: "pointer", fontSize: 20 }}>×</button>
            </div>

            {accountStatus?.activeSubscription ? (
              <GlassCard style={{ padding: 14, marginBottom: 12 }} accent={acc.secondary}>
                <div style={{ color: C.textSecondary, fontSize: fs - 1 }}>
                  {t("credits_modal_subscribed_notice")}
                </div>
              </GlassCard>
            ) : (
              <GlassCard style={{ padding: 14, marginBottom: 12 }} accent={acc.primary}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                  <div>
                    <div style={{ color: C.textPrimary, fontSize: 14, fontWeight: 600 }}>{t("credits_modal_subscription_title")}</div>
                    <div style={{ color: C.textMuted, fontSize: 12 }}>{t("credits_modal_subscription_desc")}</div>
                  </div>
                  <Button
                    variant="primary"
                    acc={acc}
                    onClick={() => {
                      gaTrackCta({
                        cta_id: "credits_modal_subscription",
                        cta_label: t("credits_modal_subscription_cta"),
                        destination: "/payment",
                        screen_class: "creditsPlans",
                        product_code: SUBSCRIPTION_PRODUCT_CODE,
                        product_type: "subscription",
                      });
                      const subscription = billingCatalog.find((item) => item.code === SUBSCRIPTION_PRODUCT_CODE);
                      // If the catalog isn't ready yet, still navigate with a fully-defined
                      // subscription product so `/payment?...` never receives `undefined`.
                      const fallbackSubscription = {
                        code: SUBSCRIPTION_PRODUCT_CODE,
                        product_type: "subscription",
                        credits_amount: 0,
                        prices: [
                          { amount_minor: 199_000, currency: "VND", billing_interval: "month" },
                          { amount_minor: 999, currency: "USD", billing_interval: "month" },
                        ],
                      };
                      setShowPaymentOptions(false);
                      onSelectSubscription?.(subscription || fallbackSubscription);
                    }}
                    style={{ padding: "8px 12px", fontSize: 12 }}
                  >
                    {`${t("credits_modal_subscription_cta")} · ${(() => {
                      const subscription = billingCatalog.find((item) => item.code === SUBSCRIPTION_PRODUCT_CODE);
                      const price = pickPrice(subscription?.prices || [], "month");
                      return price
                        ? formatMoney(price.amount_minor, price.currency)
                        : formatMoney(fallbackSubscriptionAmountMinor, fallbackCurrency);
                    })()}`}
                  </Button>
                </div>
              </GlassCard>
            )}

            <div style={{ fontSize: 12, letterSpacing: "0.12em", color: C.textMuted, marginBottom: 8 }}>{t("credits_modal_packs_title")}</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 10 }}>
              {fallbackPacks.map((pack) => (
                <GlassCard key={pack.code} style={{ padding: 14 }} accent={pack.color}>
                  <div style={{ color: pack.color, fontSize: 12, letterSpacing: "0.08em", marginBottom: 4 }}>{pack.name}</div>
                  <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 30, color: C.textPrimary }}>{pack.credits} <span style={{ fontSize: 14, color: C.textMuted }}>{t("credits")}</span></div>
                  <div style={{ color: pack.color, fontSize: 18, marginTop: 4 }}>{pack.displayPrice}</div>
                  <div style={{ marginTop: 10 }}>
                    <Button
                      variant="outline"
                      acc={acc}
                      onClick={() => {
                        gaTrackCta({
                          cta_id: `credits_modal_pack_${pack.code}`,
                          cta_label: pack.name,
                          destination: "/payment",
                          screen_class: "creditsPlans",
                          product_code: pack.code,
                          product_type: "credit_pack",
                        });
                        setShowPaymentOptions(false);
                        onSelectPack?.({
                          code: pack.code,
                          product_type: "credit_pack",
                          credits_amount: pack.credits,
                          prices: [
                            {
                              amount_minor: pack.amountMinor,
                              currency: pack.currency,
                              billing_interval: "one_time",
                            },
                          ],
                        });
                      }}
                      style={{ width: "100%", padding: "8px 10px", fontSize: 12 }}
                    >
                      {t("credits_modal_choose_pack")}
                    </Button>
                  </div>
                </GlassCard>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
